import { useNavigate } from 'react-router-dom';
import { Stack, SxProps } from '@mui/material';
import { Button } from 'framework/ui/mui-builder';
import React, { useState } from 'react';
import { useFormContext } from 'framework/form';

interface FormButtonsLayoutProps {
  additionalOnSubmitAction?: () => void;
  messageComponent?: React.ReactNode;
  onSubmitAction?: () => void;
  onCancelAction?: Nullable<() => void>;
  isCancelButtonVisible?: boolean;
  submitButtonText?: string;
  cancelButtonText?: string;
  isCustomDisabledSubmit?: boolean;
  actionsSx?: SxProps;
}

const FormButtonsLayout = ({
  additionalOnSubmitAction,
  messageComponent,
  onSubmitAction,
  onCancelAction,
  isCancelButtonVisible = true,
  submitButtonText,
  cancelButtonText,
  isCustomDisabledSubmit,
  actionsSx,
}: FormButtonsLayoutProps) => {
  const navigate = useNavigate();
  const [length] = useState(navigate.length - 1);

  const { submitForm, isSubmitEnabled, isLoadingArtifact } = useFormContext();

  const onCancel =
    onCancelAction ||
    (() => {
      navigate(length - navigate.length);
    });

  const defaultAction = () => {
    if (submitForm) {
      submitForm();
    }

    if (additionalOnSubmitAction) {
      additionalOnSubmitAction();
    }
  };

  const action = () => (onSubmitAction ? onSubmitAction() : defaultAction());

  return (
    <Stack
      className="form-buttons-wrapper"
      direction="row"
      useFlexGap
      sx={[
        {
          flexWrap: 'wrap',
          gap: '10px',
          '& .form-errors': { transition: 'color 0.2s', color: 'error.main' },
          '& .error-section-link': { display: 'block' },
        },
        ...(Array.isArray(actionsSx) ? actionsSx : actionsSx ? [actionsSx] : []),
      ]}
    >
      <Stack
        className="btn-area"
        direction="row"
        useFlexGap
        sx={{
          flex: '0 1 auto',
          flexShrink: 0,
          alignItems: 'flex-start',
          gap: '10px',
          width: 'auto',
          minWidth: '200px',
          maxWidth: 'none',
          '&& > .MuiButton-root:not(:last-child)': { mr: 0 },
        }}
      >
        <Button
          data-testid="submit-button"
          type="submit"
          onClick={action}
          disabled={!isSubmitEnabled || isLoadingArtifact || isCustomDisabledSubmit}
        >
          {submitButtonText || 'Submit'}
        </Button>
        {isCancelButtonVisible && (
          <Button data-testid="cancel-btn" variant="outlined" type="button" onClick={onCancel}>
            {cancelButtonText || 'Cancel'}
          </Button>
        )}
      </Stack>
      {messageComponent}
    </Stack>
  );
};

export default FormButtonsLayout;
